import { ReactNode } from 'react';
import { cn } from '@/lib/utils';

interface SettingsFieldProps {
  label: string;
  /** Rendered value. Falsy values fall back to `emptyText`. */
  value?: ReactNode;
  /** Shown in place of an absent value, e.g. "Not connected". */
  emptyText?: string;
  /** Identifiers (org slugs, UUIDs, usernames) read better in mono. */
  mono?: boolean;
  /** Trailing slot on the label row, e.g. a Locked badge. */
  action?: ReactNode;
  /** One line under the value. Keep it to what the user can act on. */
  hint?: ReactNode;
  className?: string;
}

/**
 * A read-only settings value.
 *
 * Deliberately NOT input-shaped. The old treatment reused an input's border and
 * radius for values nobody can edit, so the whole tab read as a form stuck in a
 * disabled state. A flat inset on `ds-gray-subtlest` says "this is data" while
 * inputs keep the bordered-on-white look, which makes the two scannable apart at
 * a glance.
 */
export function SettingsField({
  label,
  value,
  emptyText = 'Not set',
  mono = false,
  action,
  hint,
  className,
}: SettingsFieldProps) {
  const isEmpty =
    value === null || value === undefined || value === '' || value === false;

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <div className="flex min-h-5 items-center justify-between gap-2">
        <span className="ds-text-body-xs-medium text-ds-text-subtle">
          {label}
        </span>
        {action}
      </div>
      <div
        className={cn(
          'rounded-lg bg-ds-gray-subtlest px-3 py-2',
          mono ? 'ds-text-code-md' : 'ds-text-body-sm-medium',
          isEmpty ? 'text-ds-text-subtler' : 'text-ds-text-darker',
          'truncate'
        )}
        title={typeof value === 'string' ? value : undefined}
      >
        {isEmpty ? emptyText : value}
      </div>
      {hint ? (
        <span className="ds-text-body-xs-medium text-ds-text-subtle text-pretty">
          {hint}
        </span>
      ) : null}
    </div>
  );
}

export default SettingsField;
